/**
 * Trendline Automatic - TS — TradeStation "WA-TrendLines Automatic".
 *
 * One swing-strength engine per side (not the nine-class Memory walk):
 *   1. Confirm a swing high/low of strength S (strict both sides, like
 *      SwingHighBar / SwingLowBar with Length = S+1).
 *   2. On a new swing, walk the last K stored swings and take the most
 *      recent higher high (down TL) / lower low (up TL).
 *   3. Keep ONE current up line and ONE current down line. ExtRight while
 *      live. History=Yes truncates the previous line at the bar a new one
 *      is drawn (unless a delayed close-breach already truncated it).
 *      History=No drops the previous line.
 *   4. BarsPast bars after confirmation, if Close[BarsPast] is through the
 *      line value at that bar, freeze the end at the current bar.
 *
 * This is the library study, not CUE Elite Memory. Defaults match the
 * TradeStation inputs: SwHiStrength=4, SwLoStrength=4, BarsPast=10,
 * History=Yes, last-10 swing search.
 */
import type { Bar } from "@/lib/chart/indicatorTypes";

const EPS = 1e-9;

export type TlAutoTsParams = {
  swHiStrength?: unknown;
  swLoStrength?: unknown;
  barsPast?: unknown;
  history?: unknown;
  swingLookback?: unknown;
  lookback?: unknown;
};

export type TlAutoTsSeg = {
  t1: number;
  p1: number;
  t2: number;
  p2: number;
  kind: "support" | "resistance";
  role: "live" | "history";
  extendRight?: boolean;
};

const num = (v: unknown, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const flag = (v: unknown, d: boolean) =>
  v === undefined || v === null ? d : !(v === false || v === 0 || v === "false" || v === "0" || v === "off");

function lineVal(oa: number, op: number, da: number, dp: number, x: number): number {
  if (da === oa) return op;
  return op + ((dp - op) * (x - oa)) / (da - oa);
}

/** TradeStation SwingHigh / SwingLow: pivot strictly beyond `s` bars on both sides. */
function isSwing(side: "H" | "L", i: number, s: number, H: number[], L: number[]): boolean {
  if (i < s || i + s >= H.length) return false;
  for (let k = 1; k <= s; k++) {
    if (side === "H") {
      if (H[i]! <= H[i - k]! || H[i]! <= H[i + k]!) return false;
    } else if (L[i]! >= L[i - k]! || L[i]! >= L[i + k]!) {
      return false;
    }
  }
  return true;
}

type Swing = { bar: number; price: number };
type Active = {
  oBar: number;
  oPrice: number;
  dBar: number;
  dPrice: number;
  /** Dest-pivot bar (EL `DnTLEndBar` / `UpTLEndBar`). */
  endPivot: number;
  truncated: boolean;
  truncBar: number;
  truncPrice: number;
};

function findOrigin(swings: Swing[], side: "H" | "L", lookK: number): number | null {
  const n = swings.length;
  if (n < 2) return null;
  const newest = swings[0]!;
  const limit = Math.min(lookK, n - 1);
  for (let depth = 1; depth <= limit; depth++) {
    const cand = swings[depth]!;
    if (side === "H" && cand.price > newest.price) return depth;
    if (side === "L" && cand.price < newest.price) return depth;
  }
  return null;
}

function freezeAt(a: Active, i: number): void {
  a.truncated = true;
  a.truncBar = i;
  a.truncPrice = lineVal(a.oBar, a.oPrice, a.dBar, a.dPrice, i);
}

function maybeBreak(
  a: Active | null,
  i: number,
  strength: number,
  barsPast: number,
  side: "H" | "L",
  Cl: number[],
): void {
  if (!a || a.truncated) return;
  if (i <= a.endPivot + strength + barsPast) return;
  const x = i - barsPast;
  if (x < 0) return;
  const v = lineVal(a.oBar, a.oPrice, a.dBar, a.dPrice, x);
  const pierced = side === "H" ? Cl[x]! > v + EPS : Cl[x]! < v - EPS;
  if (pierced) freezeAt(a, i);
}

function toSeg(win: Bar[], a: Active, kind: "support" | "resistance"): TlAutoTsSeg {
  if (a.truncated) {
    return {
      t1: win[a.oBar]!.t,
      p1: a.oPrice,
      t2: win[a.truncBar]!.t,
      p2: a.truncPrice,
      kind,
      role: "history",
    };
  }
  return {
    t1: win[a.oBar]!.t,
    p1: a.oPrice,
    t2: win[a.dBar]!.t,
    p2: a.dPrice,
    kind,
    role: "live",
    extendRight: true,
  };
}

export function computeTrendlineAutomaticTs(bars: Bar[], p: TlAutoTsParams = {}): TlAutoTsSeg[] {
  const swHi = Math.max(1, Math.round(num(p.swHiStrength, 4)));
  const swLo = Math.max(1, Math.round(num(p.swLoStrength, 4)));
  const barsPast = Math.max(0, Math.round(num(p.barsPast, 10)));
  const history = flag(p.history, true);
  const swingK = Math.max(1, Math.round(num(p.swingLookback, 10)));
  const lookback = Math.max(0, Math.round(num(p.lookback, 5000)));
  const src = Array.isArray(bars) ? bars : [];
  const start = lookback > 0 && src.length > lookback ? src.length - lookback : 0;
  const win = src.slice(start);
  const n = win.length;
  if (n < Math.max(swHi, swLo) * 2 + 2) return [];

  const H = new Array<number>(n);
  const L = new Array<number>(n);
  const Cl = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    H[i] = win[i]!.high;
    L[i] = win[i]!.low;
    Cl[i] = win[i]!.close;
  }

  const hiSw: Swing[] = [];
  const loSw: Swing[] = [];
  let dn: Active | null = null;
  let up: Active | null = null;
  const hist: TlAutoTsSeg[] = [];

  const takeNew = (cur: Active | null, next: Active, kind: "support" | "resistance", confirmI: number): Active => {
    if (cur && history) {
      if (!cur.truncated) freezeAt(cur, confirmI);
      hist.push(toSeg(win, cur, kind));
    }
    return next;
  };

  for (let i = 0; i < n; i++) {
    if (i >= swHi && isSwing("H", i - swHi, swHi, H, L)) {
      const pb = i - swHi;
      hiSw.unshift({ bar: pb, price: H[pb]! });
      const idx = findOrigin(hiSw, "H", swingK);
      if (idx != null) {
        const o = hiSw[idx]!;
        const d = hiSw[0]!;
        dn = takeNew(
          dn,
          {
            oBar: o.bar,
            oPrice: o.price,
            dBar: d.bar,
            dPrice: d.price,
            endPivot: d.bar,
            truncated: false,
            truncBar: d.bar,
            truncPrice: d.price,
          },
          "resistance",
          i,
        );
      }
    }

    if (i >= swLo && isSwing("L", i - swLo, swLo, H, L)) {
      const pb = i - swLo;
      loSw.unshift({ bar: pb, price: L[pb]! });
      const idx = findOrigin(loSw, "L", swingK);
      if (idx != null) {
        const o = loSw[idx]!;
        const d = loSw[0]!;
        up = takeNew(
          up,
          {
            oBar: o.bar,
            oPrice: o.price,
            dBar: d.bar,
            dPrice: d.price,
            endPivot: d.bar,
            truncated: false,
            truncBar: d.bar,
            truncPrice: d.price,
          },
          "support",
          i,
        );
      }
    }

    maybeBreak(dn, i, swHi, barsPast, "H", Cl);
    maybeBreak(up, i, swLo, barsPast, "L", Cl);
  }

  const out: TlAutoTsSeg[] = history ? hist.slice() : [];
  const emitCurrent = (a: Active | null, kind: "support" | "resistance") => {
    if (!a) return;
    if (a.truncated && history) {
      const already = hist.some(
        (h) => h.kind === kind && h.t1 === win[a.oBar]!.t && h.t2 === win[a.truncBar]!.t,
      );
      if (already) return;
    }
    out.push(toSeg(win, a, kind));
  };
  emitCurrent(up, "support");
  emitCurrent(dn, "resistance");
  return out;
}
